import { useCallback, useMemo, type ChangeEvent } from 'react';
import {
  validateImageFile,
  getImageNaturalDimensions,
} from '../../utils/fileHandler';
import { slugify } from '../../utils/slugify';
import { needsClientProcessing } from '../../utils/compressImage';
import { MAX_WEBP_DIMENSION, MAX_AUTO_DIMENSION } from '../../constants';
import type { OutputFormat } from '../../types/floorPlan.types';
import type { PageRowProps } from './types';

export const PageRow = ({
  page,
  settings,
  projectId,
  planName,
  onChange,
}: PageRowProps) => {
  const handlePdfFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) {
        onChange({ pdfFile: null });
        return;
      }
      if (file.type !== 'application/pdf') {
        onChange({ pdfFile: null });
        e.target.value = '';
        return;
      }
      onChange({ pdfFile: file });
    },
    [onChange],
  );

  const handleFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) {
        onChange({ file: null, naturalWidth: null, naturalHeight: null });
        return;
      }

      const error = validateImageFile(file);
      if (error) {
        onChange({ file: null, naturalWidth: null, naturalHeight: null });
        e.target.value = '';
        return;
      }

      onChange({ file });

      const dims = await getImageNaturalDimensions(file);
      const maxDimension = Math.max(dims.width, dims.height);

      if (maxDimension > MAX_AUTO_DIMENSION) {
        const autoScaleFactor = MAX_AUTO_DIMENSION / maxDimension;
        onChange({
          file,
          naturalWidth: dims.width,
          naturalHeight: dims.height,
          scaleFactor: parseFloat(autoScaleFactor.toFixed(4)),
          quality: 90,
          outputFormat: 'webp',
        });
      } else {
        onChange({
          file,
          naturalWidth: dims.width,
          naturalHeight: dims.height,
          scaleFactor: 1,
          quality: 100,
          outputFormat: 'webp',
        });
      }
    },
    [onChange],
  );

  const fileStatus = settings.file
    ? `${settings.file.name} (${(settings.file.size / (1024 * 1024)).toFixed(2)} MB)`
    : 'No file selected';

  const fileStatusColor = settings.file ? 'text-[#3fb950]' : 'text-[#8b949e]';

  const sourceWidth = settings.naturalWidth || page.width_px;
  const sourceHeight = settings.naturalHeight || page.height_px;

  const dimsText =
    sourceWidth && sourceHeight ? `${sourceWidth} × ${sourceHeight} px` : '';

  const outputDimsText =
    sourceWidth && sourceHeight && settings.scaleFactor !== 1
      ? `→ ${Math.round(sourceWidth * settings.scaleFactor)} × ${Math.round(sourceHeight * settings.scaleFactor)} px`
      : '';

  const processingInfo = useMemo(() => {
    if (!settings.file) return null;
    const willProcess = needsClientProcessing(
      {
        scaleFactor: settings.scaleFactor,
        quality: settings.quality,
        outputFormat: settings.outputFormat,
      },
      settings.file.type,
    );
    if (!willProcess) return { status: 'passthrough' as const };

    if (
      settings.naturalWidth &&
      settings.naturalHeight &&
      settings.outputFormat === 'webp'
    ) {
      const outputWidth = Math.round(
        settings.naturalWidth * settings.scaleFactor,
      );
      const outputHeight = Math.round(
        settings.naturalHeight * settings.scaleFactor,
      );
      if (
        outputWidth > MAX_WEBP_DIMENSION ||
        outputHeight > MAX_WEBP_DIMENSION
      ) {
        return {
          status: 'webp-too-large' as const,
          outputWidth,
          outputHeight,
        };
      }
    }

    return { status: 'will-compress' as const };
  }, [
    settings.file,
    settings.scaleFactor,
    settings.quality,
    settings.outputFormat,
    settings.naturalWidth,
    settings.naturalHeight,
  ]);

  return (
    <div className="bg-[#0d1117] border border-[#30363d] rounded-md px-3 py-2.5 mb-1.5">
      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        <span className="bg-[#58a6ff] text-[#0d1117] font-bold text-[11px] px-2 py-0.5 rounded">
          Page {page.index}
        </span>
        <span className="text-[11px] text-[#8b949e]">
          {page.width_px || '?'} x {page.height_px || '?'} px
        </span>
        <span className="inline-block rounded px-2 py-0.5 text-[11px] whitespace-nowrap border bg-[#21262d] border-[#30363d] text-[#8b949e]">
          source: {page.image || 'none'}
        </span>
        {dimsText && (
          <span className="text-[11px] text-[#58a6ff] font-mono ml-auto">
            {dimsText} {outputDimsText}
          </span>
        )}
      </div>

      {/* Image section */}
      <div className="flex items-center gap-2 mb-1">
        <label className="text-[12px] text-[#8b949e] w-[100px] flex-none">
          Image Path:
        </label>
        <span className="text-[12px] text-[#8b949e] font-mono truncate">
          {`smart-surveyor/projects/${projectId}/floor_plans/${slugify(planName)}/source/page_${page.index}.${settings.outputFormat}`}
        </span>
      </div>
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <label className="text-[12px] text-[#8b949e]">Scale Factor:</label>
        <input
          type="number"
          className="bg-[#0d1117] border border-[#30363d] text-[#c9d1d9] px-2 py-1.5 rounded-md text-sm font-mono w-[100px] focus:outline-none focus:border-[#58a6ff]"
          value={settings.scaleFactor}
          step={0.5}
          min={0}
          inputMode="decimal"
          onChange={(e) => {
            const value = parseFloat(e.target.value);
            if (Number.isNaN(value)) return;
            const clamped = Math.max(0.01, value);
            onChange({ scaleFactor: clamped });
          }}
        />
        <label className="text-[12px] text-[#8b949e] ml-3">Quality:</label>
        <input
          type="number"
          className="bg-[#0d1117] border border-[#30363d] text-[#c9d1d9] px-2 py-1.5 rounded-md text-sm font-mono w-[80px] focus:outline-none focus:border-[#58a6ff]"
          value={settings.quality}
          min={1}
          max={100}
          onChange={(e) =>
            onChange({ quality: parseInt(e.target.value, 10) || 100 })
          }
        />
        <label className="text-[12px] text-[#8b949e] ml-3">Format:</label>
        <select
          className="bg-[#0d1117] border border-[#30363d] text-[#c9d1d9] px-2 py-1.5 rounded-md text-sm font-mono w-[100px] focus:outline-none focus:border-[#58a6ff]"
          value={settings.outputFormat}
          onChange={(e) =>
            onChange({ outputFormat: e.target.value as OutputFormat })
          }
        >
          <option value="webp">WebP</option>
          <option value="png">PNG</option>
          <option value="jpg">JPG</option>
        </select>
      </div>
      <div className="mt-2 flex items-center gap-2 flex-wrap">
        <label className="text-[12px] text-[#8b949e]">Image File:</label>
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="text-sm text-[#8b949e] file:mr-2 file:py-1 file:px-3 file:rounded-md file:border file:border-[#30363d] file:bg-[#21262d] file:text-[#c9d1d9] file:text-sm file:cursor-pointer"
          onChange={handleFileChange}
        />
        <span className={`text-[11px] ${fileStatusColor}`}>{fileStatus}</span>
      </div>
      {processingInfo?.status === 'will-compress' && (
        <div className="mt-2 text-[11px] text-[#d29922]">
          Image will be compressed on server before upload (scale=
          {settings.scaleFactor}, quality={settings.quality}, format=
          {settings.outputFormat})
        </div>
      )}
      {processingInfo?.status === 'passthrough' && (
        <div className="mt-2 text-[11px] text-[#8b949e]">
          Image will be uploaded as-is (no processing needed)
        </div>
      )}
      {processingInfo?.status === 'webp-too-large' && (
        <div className="mt-2 text-[11px] text-[#f85149] font-semibold">
          WebP output would be {processingInfo.outputWidth}×
          {processingInfo.outputHeight} px, exceeding the {MAX_WEBP_DIMENSION}px
          WebP limit. Reduce scale factor or switch format.
        </div>
      )}
      {/* Separator */}
      <hr className="border-[#30363d] my-2.5" />
      {/* PDF section */}
      <div className="flex items-center gap-2 mb-1">
        <label className="text-[12px] text-[#8b949e] w-[100px] flex-none">
          PDF Path:
        </label>
        <span className="text-[12px] text-[#8b949e] font-mono truncate">
          {`smart-surveyor/projects/${projectId}/floor_plans/${slugify(planName)}/source/page_${page.index}.pdf`}
        </span>
      </div>
      <div className="flex items-center gap-2 flex-wrap mt-2">
        <label className="text-[12px] text-[#8b949e]">PDF File:</label>
        <input
          type="file"
          accept=".pdf,application/pdf"
          className="text-sm text-[#8b949e] file:mr-2 file:py-1 file:px-3 file:rounded-md file:border file:border-[#30363d] file:bg-[#21262d] file:text-[#c9d1d9] file:text-sm file:cursor-pointer"
          onChange={handlePdfFileChange}
        />
        <span
          className={`text-[11px] ${settings.pdfFile ? 'text-[#3fb950]' : 'text-[#8b949e]'}`}
        >
          {settings.pdfFile
            ? `${settings.pdfFile.name} (${(settings.pdfFile.size / (1024 * 1024)).toFixed(2)} MB)`
            : 'No PDF selected'}
        </span>
      </div>
    </div>
  );
};
